import React from 'react';
import s from './categories.module.css';

// Merging two categories the studio answers to by one name (#142).
//
// #129 refuses a duplicate name in the editor (`categoryErrors` in
// domain/categories.js says why there rather than in `firestore.rules` or a
// derived id). It cannot reach two devices of one studio, both offline, both
// adding "Lesson": both commit, and the studio holds the state #129 prevents.
// MMS has no offline editing, so it has no repair flow to copy.
//
//   * Nothing is chosen on the teacher's behalf. `planCategoryMerge` refuses
//     to pick a survivor — as `canonicalSpelling` does about a tied tag and
//     `planCategoryLink` about an ambiguous key — and there is no preselected
//     radio. A merge deletes a category and re-files every lesson and student
//     under it; which spelling survives is the teacher's.
//   * The consequences are on screen before the button: what moves, what is
//     deleted, and the one invisible change (`dropsDefaultCategory`) are all
//     rendered from the plan the footer's button will commit.
//
// It decides nothing and writes nothing: the plan, the choice and the commit
// live in CategoriesDrawer beside the Edit panel's, so the teacher reads what
// the repair proposes before saying yes (as `planCategoryBackfill` argues).
// `duplicateCategoryGroups` is asked in the drawer.

// Said only when the plan says so. `dropsDefaultCategory` is the one
// consequence that is invisible afterwards: `defaultEventCategory` finds the
// built-in by id and falls back to `categories[0]`, so deleting it moves which
// category a new event opens on. Worded as what will happen rather than as a
// warning, because merging the built-in "Lesson" into the studio's own is
// reasonable.
export const DEFAULT_WARNING =
  'This deletes the category new events open on, so new events will open on '
  + 'whichever category comes first alphabetically instead.';

// The plan came back saying there is nothing to merge — another device's
// delete can land while this panel is open — and the panel says so rather
// than vanishing mid-decision.
export const STALE_GROUP =
  'These categories are no longer duplicates — the list has changed since this '
  + 'was opened. Go back and check.';

// What tells the teacher the studio is in this state at all.
//
// Above the list rather than on the offending rows, naming the rows it is
// about: two categories reachable by one name are indistinguishable in the
// list, so a marker on each would not say which pair it means when the studio
// holds more than one. It renders nothing when there are no duplicates.
export function DuplicateCategoryBanner({ groups = [], onMerge, busy = false }) {
  if (!groups.length) return null;
  return (
    <div className={s.mergeBanner}>
      {groups.length === 1
        ? 'Two categories on this studio share a name.'
        : `${groups.length} names on this studio are each used by more than one category.`}
      {' '}
      That happens when two devices both add one while offline. Merging keeps
      one and moves everything filed under the others onto it.
      {groups.map((g) => (
        <div key={g.key} className={s.mergeBannerRow}>
          {/* The members' own spellings, joined, rather than the folded key:
              the key is lower-cased and whitespace-collapsed, so printing it
              would show the teacher a name neither category actually has. */}
          <span className={s.mergeBannerName}>
            {g.members.map((c) => `“${c.label}”`).join(' and ')}
          </span>
          <button
            type="button"
            className={s.mergeBannerAction}
            disabled={busy}
            onClick={() => onMerge(g.key)}
          >
            Merge
          </button>
        </div>
      ))}
    </div>
  );
}

export default function MergeCategoriesPanel({
  group, plan, keepId, onChoose, refusal = '', stale = false, busy = false,
}) {
  const members = group?.members || [];

  return (
    <>
      <div className={s.lead}>
        Two categories on this studio answer to the same name. That happens when
        two devices both add it while offline — neither could see the other.
        Merging them keeps one name and moves everything filed under the other
        onto it.
      </div>

      <fieldset className={s.mergeChoice}>
        <legend className={s.mergeLegend}>Keep which one?</legend>
        {members.map((c) => (
          <label key={c.id} className={s.mergeOption}>
            <input
              type="radio"
              name="merge-keep"
              value={c.id}
              checked={keepId === c.id}
              disabled={busy}
              onChange={() => onChoose(c.id)}
            />
            <span className={s.swatch} style={{ background: c.color }} />
            {/* Quoted, because the two names differ only by case or by spacing
                and an unquoted "Lesson " beside "Lesson" is unreadable. */}
            <span className={s.mergeName}>{`“${c.label}”`}</span>
          </label>
        ))}
      </fieldset>

      {refusal ? <p className={s.mergeError}>{refusal}</p> : null}

      {plan && plan.verdict === 'merge' ? (
        <div className={s.mergeSummary}>
          <p className={s.mergeLine}>
            {plan.counts.writes === 0
              ? 'Nothing is filed under the other category, so only the category itself is deleted.'
              : `${plan.counts.writes} ${plan.counts.writes === 1 ? 'lesson or student moves' : 'lessons and students move'} onto “${plan.keep.label}”.`}
          </p>
          {plan.remove.map((c) => (
            <p key={c.id} className={s.mergeLine}>{`“${c.label}” will be deleted.`}</p>
          ))}
          {plan.dropsDefaultCategory ? (
            <p className={s.mergeNotice}>{DEFAULT_WARNING}</p>
          ) : null}
        </div>
      ) : null}

      {stale || (plan && plan.verdict !== 'merge') ? (
        <p className={s.mergeError}>{STALE_GROUP}</p>
      ) : null}
    </>
  );
}
